import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { usePermissions } from "@/hooks/usePermissions";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

interface Props {
  children: React.ReactNode;
  allowedRoles: Roles[];
  // Optional granular permissions. When provided they take over the decision:
  // access requires one of these permission values (e.g. "inventory.view") and
  // allowedRoles is ignored. Full-access users (owner, support, admin, primary
  // manager) pass every permission check, but a secondary manager does not —
  // their ROLE_MANAGER must not leak access their permissions deny.
  allowedPermissions?: string[];
}

const RoleProtectedPage: React.FC<Props> = ({
  children,
  allowedRoles,
  allowedPermissions,
}) => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const { canAny } = usePermissions();
  const location = useLocation();

  // A loaded user always has roles. Treat a missing user OR an empty user
  // object (the transient state right after logout / mid-login) as
  // unauthenticated, so we never read `.roles` off undefined and 500-crash a
  // guarded page.
  if (!currentUser?.roles?.length) {
    return <Navigate to="/auth/login" replace />;
  }

  const isAllowed = allowedPermissions?.length
    ? canAny(...allowedPermissions)
    : currentUser.roles.some((role) => allowedRoles.includes(role));

  if (!isAllowed) {
    if (location.pathname === "/") {
      return <Navigate to="/orders" replace />;
    }

    return <Navigate to="/no-access" replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedPage;
